import React, { useEffect } from 'react';
import {
  ArrowRightIcon,
  CheckIcon,
  CloseIcon,
  CopyIcon,
  DownloadIcon,
  MinusIcon,
  PlusIcon,
  QuoteIcon,
} from './icons';
import {
  capitalize,
  displayMembershipCode,
} from '../../../assets/helpers/funciones';
import { codeTypeEnum } from '../../../assets/helpers/options';
import { discountCodeMaxLength } from '../../../assets/helpers/const';
import './CotizadorPlanes.scss';

const getPlanTheme = (code) => {
  const displayed = displayMembershipCode(code);
  if (['GOLD', 'GOLDM', 'GOLDENP', 'GOLDF4'].includes(code) || displayed === 'GOLD') {
    return 'gold';
  }
  if (['PLUS', 'PLUSM', 'PLUSF4'].includes(code) || displayed === 'PLUS') {
    return 'plus';
  }
  if (['CLASSIC', 'CLASSICM'].includes(code) || displayed === 'CLASSIC') {
    return 'classic';
  }
  return 'other';
};

const formatUsd = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const controlClass = (error) =>
  `cotizador-field__control${error ? ' is-invalid' : ''}`;

const FieldError = ({ error }) =>
  error ? <span className="cotizador-field__error">{error}</span> : null;

// Avisos de recargo por antigüedad: solo aplican con las condiciones anteriores
const yearMessage = (year, armorType, newPlanRules) => {
  if (newPlanRules) return null;
  const parsedYear = parseFloat(year);
  let text = '';

  if (parsedYear >= 2000 && parsedYear <= 2005) {
    text =
      'NOTA: El plan tendrá un precio especial por el año del vehículo (se añadirá +20% al precio final)';
  }

  if (parsedYear < 2000 && parsedYear >= 1990) {
    text =
      'NOTA: El plan tendrá un precio especial por el año del vehículo (se añadirá +50% al precio final)';
  }

  if (text && (!armorType || armorType === 'ninguno')) {
    return <p className="cotizador-field__note">{text}</p>;
  }

  return null;
};

/**
 * CotizadorPlanesModal
 * ---------------------------------------------------------------------------
 * Modal de cotización de membresías. Recibe el estado y las acciones desde
 * `useMembershipQuote`, igual que CalculadoraBCVModal con useCurrencyConverter.
 */
export const CotizadorPlanesModal = ({ quote }) => {
  const {
    isOpen,
    closeModal,
    quoteDate,
    handleQuoteDateChange,
    quoteDateError,
    isLoadingRate,
    isLoadingCatalogs,
    vehicles,
    vehicleCount,
    addVehicle,
    removeVehicle,
    handleVehicleFieldChange,
    preloadRandomVehicle,
    canPreloadVehicle,
    handleVehicleBlur,
    selectPlan,
    handleCodeTypeChange,
    handleDiscountInputChange,
    applyDiscount,
    removeDiscount,
    codeTypeOptions,
    vehicleClasses,
    armorTypeOptions,
    vehicleWeightOptions,
    kmOptions,
    newPlanRules,
    maxYear,
    minYear,
    maxVehicles,
    canUseExtraFlags,
    copied,
    handleCopy,
    handleDownload,
    isDownloading,
    handleGenerateOrder,
    quotedCount,
    canExportQuote,
    totalUsdFormatted,
    totalBsFormatted,
    todayISO,
    codeMaxLength,
  } = quote;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  if (!isOpen) return null;

  return (
    <div
      className="cotizador-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeModal();
      }}
    >
      <div
        className="cotizador-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Cotizador de planes"
      >
        <div className="cotizador-modal__header">
          <span className="cotizador-modal__header-icon">
            <QuoteIcon />
          </span>
          <div className="cotizador-modal__header-text">
            <h3 className="cotizador-modal__title">Cotizador de planes</h3>
            <p className="cotizador-modal__subtitle">
              Precio según año, clase y uso del vehículo
            </p>
          </div>
          <button
            type="button"
            className="cotizador-modal__close"
            onClick={closeModal}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="cotizador-modal__body">
          <div className="cotizador-toolbar">
            <div className="cotizador-toolbar__row">
              <p className="cotizador-toolbar__label">Cantidad de vehículos</p>
              <div className="cotizador-stepper">
                <button
                  type="button"
                  className="cotizador-stepper__btn"
                  onClick={removeVehicle}
                  disabled={vehicleCount <= 1}
                  aria-label="Quitar vehículo"
                >
                  <MinusIcon />
                </button>
                <span className="cotizador-stepper__value">{vehicleCount}</span>
                <button
                  type="button"
                  className="cotizador-stepper__btn"
                  onClick={addVehicle}
                  disabled={vehicleCount >= maxVehicles}
                  aria-label="Agregar vehículo"
                >
                  <PlusIcon />
                </button>
              </div>
            </div>
            <div className="cotizador-toolbar__row">
              <p className="cotizador-toolbar__label">Fecha de cotización</p>
              <div>
                <input
                  className={`cotizador-date${quoteDateError ? ' is-invalid' : ''}`}
                  type="date"
                  value={quoteDate}
                  onChange={handleQuoteDateChange}
                  max={todayISO}
                />
                <FieldError error={quoteDateError} />
              </div>
            </div>
          </div>

          {vehicles.map((vehicle, index) => {
            const errors = vehicle.errors || {};
            const codeErrors = vehicle.codeErrors || {};
            const codeLength =
              vehicle.codeType === codeTypeEnum.GIFTCARD
                ? 6
                : codeMaxLength || discountCodeMaxLength;

            return (
              <article key={vehicle.id} className="cotizador-vehicle">
                <div className="cotizador-vehicle__head">
                  <h4
                    className={`cotizador-vehicle__title${
                      canPreloadVehicle ? ' cotizador-vehicle__title--debug' : ''
                    }`}
                    onClick={
                      canPreloadVehicle
                        ? () => preloadRandomVehicle(vehicle.id)
                        : undefined
                    }
                  >
                    Vehículo {index + 1}
                  </h4>
                  {vehicle.selectedMembership ? (
                    <div
                      className={`cotizador-vehicle__price${
                        vehicle.hasDiscount ? ' cotizador-vehicle__price--discounted' : ''
                      }`}
                    >
                      {vehicle.hasDiscount && (
                        <span className="cotizador-vehicle__price-old">
                          {formatUsd(vehicle.originalPrice)}
                        </span>
                      )}
                      <span>{formatUsd(vehicle.finalPrice)}</span>
                    </div>
                  ) : null}
                </div>

                <div className="cotizador-fields">
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Año del vehículo *</span>
                    <input
                      className={controlClass(errors.year)}
                      type="number"
                      min={minYear}
                      max={maxYear}
                      value={vehicle.year}
                      onChange={(event) =>
                        handleVehicleFieldChange(vehicle.id, 'year', event.target.value)
                      }
                      onBlur={() => handleVehicleBlur(vehicle.id, 'year')}
                    />
                    <FieldError error={errors.year} />
                    {yearMessage(vehicle.year, vehicle.armorType, newPlanRules)}
                  </label>
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Clase de vehículo *</span>
                    <select
                      className={controlClass(errors.class)}
                      value={vehicle.classId}
                      onChange={(event) =>
                        handleVehicleFieldChange(vehicle.id, 'classId', event.target.value)
                      }
                      onBlur={() => handleVehicleBlur(vehicle.id, 'classId')}
                    >
                      <option value=""></option>
                      {vehicleClasses.map((option) => (
                        <option key={option._id} value={option._id}>
                          {capitalize(option.name)}
                        </option>
                      ))}
                    </select>
                    <FieldError error={errors.class} />
                  </label>
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Tipo RCV *</span>
                    <select
                      className={controlClass(errors.rcv)}
                      value={vehicle.rcv}
                      onChange={(event) =>
                        handleVehicleFieldChange(vehicle.id, 'rcv', event.target.value)
                      }
                      onBlur={() => handleVehicleBlur(vehicle.id, 'rcv')}
                    >
                      {vehicle.rcvOptions.map((option) => (
                        <option key={option._id} value={option._id}>
                          {capitalize(option.name)}
                        </option>
                      ))}
                    </select>
                    <FieldError error={errors.rcv} />
                  </label>
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Uso del vehículo *</span>
                    <select
                      className={controlClass(errors.vehicleUsage)}
                      value={vehicle.vehicleUsage}
                      onChange={(event) =>
                        handleVehicleFieldChange(
                          vehicle.id,
                          'vehicleUsage',
                          event.target.value
                        )
                      }
                      onBlur={() => handleVehicleBlur(vehicle.id, 'vehicleUsage')}
                    >
                      <option value=""></option>
                      {vehicle.usageOptions.map((option) => (
                        <option key={option._id} value={option._id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <FieldError error={errors.vehicleUsage} />
                  </label>
                </div>

                <div className="cotizador-fields cotizador-fields--extra">
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Blindaje</span>
                    <select
                      className={controlClass(errors.armorType)}
                      value={vehicle.armorType}
                      onChange={(event) =>
                        handleVehicleFieldChange(vehicle.id, 'armorType', event.target.value)
                      }
                      onBlur={() => handleVehicleBlur(vehicle.id, 'armorType')}
                    >
                      {armorTypeOptions.map((option) => (
                        <option key={option._id || 'none'} value={option._id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <FieldError error={errors.armorType} />
                    {!newPlanRules &&
                      vehicle.armorType &&
                      armorTypeOptions.find((option) => option._id === vehicle.armorType)
                        ?.name !== 'No' && (
                        <p className="cotizador-field__note">
                          NOTA: Tienes un monto adicional por el peso de tu vehiculo
                        </p>
                      )}
                  </label>
                  {vehicle.isTruck && (
                    <label className="cotizador-field">
                      <span className="cotizador-field__label">Peso del vehículo *</span>
                      <select
                        className={controlClass(errors.truckWeight)}
                        value={vehicle.truckWeight}
                        onChange={(event) =>
                          handleVehicleFieldChange(
                            vehicle.id,
                            'truckWeight',
                            event.target.value
                          )
                        }
                        onBlur={() => handleVehicleBlur(vehicle.id, 'truckWeight')}
                      >
                        <option value=""></option>
                        {vehicleWeightOptions.map((option) => (
                          <option key={option._id} value={option._id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      <FieldError error={errors.truckWeight} />
                    </label>
                  )}
                  {vehicle.isGold && !newPlanRules && (
                    <label className="cotizador-field">
                      <span className="cotizador-field__label">Km de servicio de grúa *</span>
                      <select
                        className={controlClass(errors.km)}
                        value={vehicle.km ? String(vehicle.km) : ''}
                        onChange={(event) =>
                          handleVehicleFieldChange(vehicle.id, 'km', event.target.value)
                        }
                        onBlur={() => handleVehicleBlur(vehicle.id, 'km')}
                      >
                        {kmOptions.map((option) => (
                          <option key={option._id} value={String(option._id)}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                      <FieldError error={errors.km} />
                    </label>
                  )}
                </div>

                {(vehicle.allowsUnlimitedTow || canUseExtraFlags) && (
                  <div className="cotizador-checks">
                    {vehicle.allowsUnlimitedTow && (
                      <label className="cotizador-check">
                        <input
                          type="checkbox"
                          checked={Boolean(vehicle.useExtendedTow)}
                          onChange={(event) =>
                            handleVehicleFieldChange(
                              vehicle.id,
                              'useExtendedTow',
                              event.target.checked
                            )
                          }
                        />
                        {newPlanRules
                          ? 'Servicios de grúa ilimitados'
                          : 'Servicio de grúa ilimitado'}
                      </label>
                    )}
                    {canUseExtraFlags && (
                      <>
                        <label className="cotizador-check">
                          <input
                            type="checkbox"
                            checked={Boolean(vehicle.useMedCert)}
                            onChange={(event) =>
                              handleVehicleFieldChange(
                                vehicle.id,
                                'useMedCert',
                                event.target.checked
                              )
                            }
                          />
                          Certificado médico
                        </label>
                        <label className="cotizador-check">
                          <input
                            type="checkbox"
                            checked={Boolean(vehicle.hasRcvCard)}
                            onChange={(event) =>
                              handleVehicleFieldChange(
                                vehicle.id,
                                'hasRcvCard',
                                event.target.checked
                              )
                            }
                          />
                          Tarjeta RCV
                        </label>
                      </>
                    )}
                  </div>
                )}

                {vehicle.isLoading || isLoadingCatalogs ? (
                  <p className="cotizador-loading">Consultando planes...</p>
                ) : null}

                {vehicle.membershipOptions.length > 0 ? (
                  <div className={`cotizador-plans${errors.selectedMembershipId ? ' is-invalid' : ''}`}>
                    {vehicle.membershipOptions.map((membership) => {
                      const theme = getPlanTheme(membership.code);
                      const selected = membership._id === vehicle.selectedMembershipId;
                      return (
                        <button
                          key={membership._id}
                          type="button"
                          className={`cotizador-plan cotizador-plan--${theme}${
                            selected ? ' is-selected' : ''
                          }`}
                          onClick={() => selectPlan(vehicle.id, membership)}
                        >
                          <span className="cotizador-plan__name">
                            {capitalize(displayMembershipCode(membership.code))}
                          </span>
                          <span className="cotizador-plan__price">
                            {formatUsd(membership.finalPrice)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  String(vehicle.year).length >= 4 &&
                  vehicle.classId &&
                  !vehicle.isLoading && (
                    <p className="cotizador-empty">
                      No hay planes disponibles para estos datos
                    </p>
                  )
                )}
                <FieldError error={errors.selectedMembershipId} />

                <div className="cotizador-discount">
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Tipo de código</span>
                    <select
                      className={controlClass(codeErrors.codeType)}
                      value={vehicle.codeType}
                      onChange={(event) =>
                        handleCodeTypeChange(vehicle.id, event.target.value)
                      }
                    >
                      <option value=""></option>
                      {codeTypeOptions.map((option) => (
                        <option key={option._id} value={option._id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                    <FieldError error={codeErrors.codeType} />
                  </label>
                  <label className="cotizador-field">
                    <span className="cotizador-field__label">Código</span>
                    <input
                      className={controlClass(codeErrors.code)}
                      type="text"
                      maxLength={codeLength}
                      value={vehicle.discountInput}
                      onChange={(event) =>
                        handleDiscountInputChange(vehicle.id, event.target.value)
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          applyDiscount(vehicle.id);
                        }
                      }}
                    />
                    <FieldError error={codeErrors.code} />
                  </label>
                  <button
                    type="button"
                    className="cotizador-discount__apply"
                    onClick={() => applyDiscount(vehicle.id)}
                    disabled={vehicle.isApplyingDiscount}
                  >
                    {vehicle.isApplyingDiscount ? '...' : 'Canjear'}
                  </button>
                </div>

                {vehicle.discountCode && (
                  <div className="cotizador-discount-banner">
                    <span>✓ {vehicle.discountLabel}</span>
                    <button
                      type="button"
                      className="cotizador-discount-banner__remove"
                      onClick={() => removeDiscount(vehicle.id)}
                    >
                      Quitar
                    </button>
                  </div>
                )}
                {vehicle.giftCard && (
                  <div className="cotizador-discount-banner">
                    <span>✓ {vehicle.giftcardLabel}</span>
                    <button
                      type="button"
                      className="cotizador-discount-banner__remove"
                      onClick={() => removeDiscount(vehicle.id)}
                    >
                      Quitar
                    </button>
                  </div>
                )}
              </article>
            );
          })}

          <div className="cotizador-total">
            <div>
              <p className="cotizador-total__label">TOTAL GENERAL</p>
              <p className="cotizador-total__meta">
                {quotedCount} {quotedCount === 1 ? 'vehículo cotizado' : 'vehículos cotizados'}
              </p>
            </div>
            <div>
              <p className="cotizador-total__amount">{totalUsdFormatted}</p>
              <p className="cotizador-total__bs">
                {isLoadingRate ? 'Cargando tasa...' : `Bs. ${totalBsFormatted}`}
              </p>
            </div>
          </div>

          <div className="cotizador-actions">
            <button
              type="button"
              className="cotizador-btn cotizador-btn--ghost"
              onClick={handleCopy}
              disabled={!canExportQuote}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
            <button
              type="button"
              className="cotizador-btn cotizador-btn--ghost"
              onClick={handleDownload}
              disabled={!canExportQuote || isDownloading}
            >
              <DownloadIcon />
              {isDownloading ? 'Descargando...' : 'Descargar PDF cotización'}
            </button>
            <button
              type="button"
              className="cotizador-btn cotizador-btn--primary"
              onClick={handleGenerateOrder}
            >
              Generar orden
              <ArrowRightIcon />
            </button>
          </div>

          <p className="cotizador-footnote">
            Cotización válida por 24h · se genera un ID al confirmar la orden
          </p>
        </div>
      </div>
    </div>
  );
};

export default CotizadorPlanesModal;
